import React, { useState } from 'react'
import { HeartIcon, EmojiHappyIcon, EmojiSadIcon, FireIcon } from '@heroicons/react/outline'
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/solid'
import useQuery from '@/hooks/useQuery'

const SocialReactions = ({ post, onReactionUpdate }) => {
  const [isReacting, setIsReacting] = useState(false)
  const { putpostRequest } = useQuery()

  const reactionIcons = {
    like: HeartIcon,
    laugh: EmojiHappyIcon,
    angry: FireIcon,
    sad: EmojiSadIcon
  }

  const reactionColors = {
    like: 'text-red-500',
    laugh: 'text-yellow-500',
    angry: 'text-orange-500',
    sad: 'text-blue-500'
  }

  const handleReaction = (reactionType) => {
    if (isReacting) return
    
    setIsReacting(true)
    putpostRequest(`/api/v3/social_posts/${post.id}/social_reactions/toggle`, 'POST', {
      reaction_type: reactionType
    }, (err, response) => {
      setIsReacting(false)
      if (err) return
      
      const updatedPost = {
        ...post,
        user_reaction: response.reaction,
        reactions_count: response.reactions_count,
        reaction_counts: response.reaction_counts || {}
      }
      
      if (onReactionUpdate) onReactionUpdate(updatedPost)
    })
  }

  const getUserReactionIcon = () => {
    if (!post.user_reaction) return HeartIcon
    return reactionIcons[post.user_reaction] || HeartIcon
  }

  const getUserReactionColor = () => {
    if (!post.user_reaction) return 'text-gray-500 hover:text-red-500'
    return reactionColors[post.user_reaction] || 'text-gray-500'
  }

  const ReactionIcon = getUserReactionIcon()

  return (
    <div className="flex items-center space-x-4">
      <div className="relative group">
        <button
          onClick={() => handleReaction('like')}
          disabled={isReacting}
          className={`flex items-center space-x-2 transition-colors ${getUserReactionColor()}`}
        >
          <ReactionIcon className="h-5 w-5" />
          <span className="text-sm">{post.reactions_count || 0}</span>
        </button>

        {/* Reaction picker dropdown */}
        <div className="absolute bottom-full -left-4 pb-0 hidden group-hover:block z-10">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-2 flex space-x-2">
          {Object.entries(reactionIcons).map(([type, Icon]) => (
            <button
              key={type}
              onClick={() => handleReaction(type)}
              disabled={isReacting}
              className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${reactionColors[type]}`}
              title={type}
            >
              <Icon className="h-5 w-5" />
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* Reaction counts breakdown */}
      {post.reaction_counts && Object.keys(post.reaction_counts).length > 0 && (
        <div className="flex items-center space-x-1">
          {Object.entries(post.reaction_counts).map(([type, count]) => {
            const Icon = reactionIcons[type]
            return count > 0 ? (
              <div key={type} className="flex items-center space-x-1">
                <Icon className={`h-4 w-4 ${reactionColors[type]}`} />
                <span className="text-xs text-gray-500">{count}</span>
              </div>
            ) : null
          })}
        </div>
      )}
    </div>
  )
}

export default SocialReactions
